import React, {
  useState,
  useEffect,
  useRef,
  forwardRef,
  useImperativeHandle,
  useCallback,
} from "react";
import { useNavigate } from "react-router-dom";
import { Search, Filter, MoreVertical, Plus, X, Copy } from "lucide-react";
import ReactDOM from "react-dom";
import MealPlanDuplicateAndTimingsPopup from "../Meal_Creation/MealPlanDuplicateAndTimingsPopup";
import { ToastContainer, useToast } from "../../Ui_Components/ToastMessage";
import {
  getMealPlanTypeList,
  getMealPlanDetails,
  deleteMealPlan,
  getMealPlanDetail,
} from "../../../services/mealPlans";
import { suggestPlanGlobalTemplate } from "../../../services/globalTemplates";
import { useAuth } from "../../../context/auth/AuthContext";
import useCustomScrollbar from "../../styles/useCustomScrollbar";
import PermissionGate from "../../Auth/PermissionGate";
import { usePermissions } from "../../../hooks/usePermissions";
import { Button } from "../../Ui_Components/Button";

interface MealPlanTabProps {
  userId: number;
  isDeactivated?: boolean;
}

interface MealPlan {
  id: number;
  plan_name: string;
  plan_code: string;
  meal_type: string;
  created_by: string;
  created_on: string;
  cal: number;
  adj_prot: number;
  carb: number;
  fat: number;
  gl: number;
  status: "Active" | "Approval Pending" | "Draft" | "In-Active";
}

interface DropdownPosition {
  top?: number;
  bottom?: number;
  left: number;
}

// Export the ref handle type so PlansPage can use it
export interface MealPlanTabHandle {
  refresh: () => void;
}

// Shimmer animation styles
const shimmerStyles = `
  @keyframes shimmer {
    0% {
      background-position: -1000px 0;
    }
    100% {
      background-position: 1000px 0;
    }
  }

  .shimmer {
    animation: shimmer 2s infinite;
    background: linear-gradient(
      to right,
      #f0f0f0 0%,
      #e0e0e0 20%,
      #f0f0f0 40%,
      #f0f0f0 100%
    );
    background-size: 1000px 100%;
  }

  .smart-tooltip {
    position: fixed;
    background-color: #1F1F1F;
    color: white;
    padding: 8px 12px;
    border-radius: 6px;
    font-size: 12px;
    white-space: nowrap;
    z-index: 1000;
    pointer-events: none;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
    opacity: 0;
    transition: opacity 0.2s;
  }

  .smart-tooltip.visible {
    opacity: 1;
  }

  .smart-tooltip::before {
    content: "";
    position: absolute;
    border-width: 5px;
    border-style: solid;
  }

  .smart-tooltip.position-bottom-right::before {
    top: -10px;
    left: 12px;
    border-color: transparent transparent #1F1F1F transparent;
  }

  .smart-tooltip.position-top-right::before {
    bottom: -10px;
    left: 12px;
    border-color: #1F1F1F transparent transparent transparent;
  }

  .smart-tooltip.position-bottom-left::before {
    top: -10px;
    right: 12px;
    border-color: transparent transparent #1F1F1F transparent;
  }

  .smart-tooltip.position-top-left::before {
    bottom: -10px;
    right: 12px;
    border-color: #1F1F1F transparent transparent transparent;
  }

  .dropdown-menu {
    position: fixed;
    background: white;
    border: 1px solid #E0E0E0;
    border-radius: 8px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    z-index: 1000;
    min-width: 140px;
    overflow: hidden;
  }

  .dropdown-item {
    padding: 10px 16px;
    font-size: 14px;
    font-family: 'Inter', sans-serif;
    color: #1F1F1F;
    cursor: pointer;
    transition: background-color 0.2s;
    border: none;
    background: none;
    width: 100%;
    text-align: left;
  }

  .dropdown-item:hover {
    background-color: #F9FEFE;
  }

  .dropdown-item.delete {
    color: #FF3B30;
  }

  .dropdown-divider {
    height: 1px;
    background-color: #F3F4F6;
    margin: 0;
  }
`;

// Smart Tooltip component
const SmartTooltipCell: React.FC<{
  children: React.ReactNode;
  value: string;
}> = ({ children, value }) => {
  const [showTooltip, setShowTooltip] = useState(false);
  const [isTruncated, setIsTruncated] = useState(false);
  const [tooltipStyle, setTooltipStyle] = useState<React.CSSProperties>({});
  const [tooltipPosition, setTooltipPosition] = useState<string>(
    "position-bottom-right",
  );
  const textRef = useRef<HTMLDivElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const checkTruncation = () => {
      if (textRef.current) {
        const isTrunc =
          textRef.current.scrollWidth > textRef.current.clientWidth;
        setIsTruncated(isTrunc);
      }
    };

    checkTruncation();
    window.addEventListener("resize", checkTruncation);
    return () => window.removeEventListener("resize", checkTruncation);
  }, [value]);

  const handleMouseEnter = () => {
    if (isTruncated && textRef.current) {
      const rect = textRef.current.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      const tooltipWidth = 200;
      const tooltipHeight = 40;
      const offset = 8;

      let top = 0;
      let left = 0;
      let position = "position-bottom-right";

      const spaceRight = viewportWidth - rect.right;
      const spaceLeft = rect.left;
      const preferRight = spaceRight > tooltipWidth || spaceRight > spaceLeft;

      const spaceBelow = viewportHeight - rect.bottom;
      const spaceAbove = rect.top;
      const preferBelow = spaceBelow > tooltipHeight || spaceBelow > spaceAbove;

      if (preferBelow) {
        top = rect.bottom + offset;
        if (preferRight) {
          left = rect.left;
          position = "position-bottom-right";
        } else {
          left = rect.right - tooltipWidth;
          position = "position-bottom-left";
        }
      } else {
        top = rect.top - tooltipHeight - offset;
        if (preferRight) {
          left = rect.left;
          position = "position-top-right";
        } else {
          left = rect.right - tooltipWidth;
          position = "position-top-left";
        }
      }

      setTooltipStyle({ top: `${top}px`, left: `${left}px` });
      setTooltipPosition(position);
      setShowTooltip(true);
    }
  };

  const handleMouseLeave = () => {
    setShowTooltip(false);
  };

  return (
    <>
      <div
        ref={textRef}
        className="truncate cursor-default"
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        {children}
      </div>
      {isTruncated && showTooltip && (
        <div
          ref={tooltipRef}
          className={`smart-tooltip visible ${tooltipPosition}`}
          style={tooltipStyle}
        >
          {value}
        </div>
      )}
    </>
  );
};

const MealPlanTab = forwardRef<MealPlanTabHandle, MealPlanTabProps>(
  ({ userId, isDeactivated = false }, ref) => {
    useCustomScrollbar();
    const navigate = useNavigate();
    const { toasts, removeToast, success, error } = useToast();
    const { user } = useAuth();
    const { can } = usePermissions();
    const [activeFilter, setActiveFilter] = useState("all");
    const [searchQuery, setSearchQuery] = useState("");
    const [mealPlans, setMealPlans] = useState<MealPlan[]>([]);
    const [loading, setLoading] = useState(true);
    const [apiError, setApiError] = useState<string | null>(null);
    const [isCreatePopupOpen, setIsCreatePopupOpen] = useState(false);
    const [nextPlanName, setNextPlanName] = useState("");
    const [openDropdownId, setOpenDropdownId] = useState<number | null>(null);
    const [dropdownPosition, setDropdownPosition] = useState<DropdownPosition>({
      top: 0,
      left: 0,
    });
    const DROPDOWN_HEIGHT = 92;
    const dropdownRef = useRef<HTMLDivElement>(null);

    const [isDeletePopupOpen, setIsDeletePopupOpen] = useState(false);
    const [planToDelete, setPlanToDelete] = useState<MealPlan | null>(null);
    const [isDeleting, setIsDeleting] = useState(false);

    const [isDuplicatePopupOpen, setIsDuplicatePopupOpen] = useState(false);
    const [planToDuplicate, setPlanToDuplicate] = useState<MealPlan | null>(
      null,
    );
    const [isDuplicating, setIsDuplicating] = useState(false);
    const [duplicatePlanData, setDuplicatePlanData] = useState<any>(null);

    const [isSuggestTemplateOpen, setIsSuggestTemplateOpen] = useState(false);
    const [planToSuggest, setPlanToSuggest] = useState<MealPlan | null>(null);
    const [isSuggesting, setIsSuggesting] = useState(false);
    const [suggestTemplateName, setSuggestTemplateName] = useState("");
    const [suggestionNote, setSuggestionNote] = useState("");

    // ── Filter dropdown state ────────────────────────────────────────────────────
    const [isFilterOpen, setIsFilterOpen] = useState(false);
    const [filterPlanTypes, setFilterPlanTypes] = useState<string[]>([]);
    const [selectedMealTypeFilters, setSelectedMealTypeFilters] = useState<
      string[]
    >([]);
    const [isFilterTypesLoading, setIsFilterTypesLoading] = useState(false);
    const filterDropdownRef = useRef<HTMLDivElement>(null);
    const filterButtonRef = useRef<HTMLButtonElement>(null);
    const [filterDropdownPosition, setFilterDropdownPosition] = useState<{
      top: number;
      left: number;
    }>({ top: 0, left: 0 });
    // ────────────────────────────────────────────────────────────────────────────

    const isCreatedByCurrentUser = (createdBy: string): boolean => {
      if (!user) return false;
      const currentUserFullName =
        `${user.nutrist_fname || ""} ${user.nutrist_lname || ""}`.trim();
      return createdBy.toLowerCase() === currentUserFullName.toLowerCase();
    };

    // Close row-action dropdown on outside click
    useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
        if (
          dropdownRef.current &&
          !dropdownRef.current.contains(event.target as Node)
        ) {
          setOpenDropdownId(null);
        }
      };

      if (openDropdownId !== null) {
        document.addEventListener("mousedown", handleClickOutside);
        return () =>
          document.removeEventListener("mousedown", handleClickOutside);
      }
    }, [openDropdownId]);

    // ── Fetch plan types for filter on mount ────────────────────────────────────
    useEffect(() => {
      const fetchFilterPlanTypes = async () => {
        setIsFilterTypesLoading(true);
        try {
          const data = await getMealPlanTypeList();
          if (data.success === "1" && Array.isArray(data.meal_plan_data)) {
            setFilterPlanTypes(
              data.meal_plan_data.map(
                (item: { meal_plan_type_name: string }) =>
                  item.meal_plan_type_name,
              ),
            );
          }
        } catch (err) {
          console.error("Failed to fetch filter plan types:", err);
        } finally {
          setIsFilterTypesLoading(false);
        }
      };
      fetchFilterPlanTypes();
    }, []);

    // ── Close filter dropdown on outside click ──────────────────────────────────
    useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
        if (
          filterDropdownRef.current &&
          !filterDropdownRef.current.contains(event.target as Node) &&
          filterButtonRef.current &&
          !filterButtonRef.current.contains(event.target as Node)
        ) {
          setIsFilterOpen(false);
        }
      };
      if (isFilterOpen) {
        document.addEventListener("mousedown", handleClickOutside);
      }
      return () =>
        document.removeEventListener("mousedown", handleClickOutside);
    }, [isFilterOpen]);
    // ────────────────────────────────────────────────────────────────────────────

    // Fetch meal plans
    const fetchMealPlans = useCallback(
      async (silent = false) => {
        if (!userId) {
          setApiError("No user ID provided");
          setLoading(false);
          return;
        }

        try {
          if (!silent) {
            setLoading(true);
          }
          setApiError(null);

          console.log("🔍 Fetching meal plans for user:", userId);

          const apiResponse = await getMealPlanDetails(userId);
          console.log("✅ Meal plans data received:", apiResponse);

          if (apiResponse.success === "1" && apiResponse.data) {
            const mappedPlans: MealPlan[] = apiResponse.data.map(
              (plan: any) => {
                let status:
                  | "Active"
                  | "Approval Pending"
                  | "Draft"
                  | "In-Active" = "Draft";
                switch (plan.meal_status) {
                  case "6":
                    status = "Approval Pending";
                    break;
                  case "2":
                    status = "Active";
                    break;
                  case "3":
                    status = "In-Active";
                    break;
                  default:
                    status = "Draft";
                }

                const formatDate = (dateStr: string) => {
                  const date = new Date(dateStr);
                  return date
                    .toLocaleDateString("en-GB", {
                      day: "2-digit",
                      month: "short",
                      year: "numeric",
                    })
                    .replace(/ /g, " ");
                };

                return {
                  id: plan.meal_plan_id,
                  plan_name: plan.plan_name,
                  plan_code: plan.plan_code || "",
                  meal_type: plan.plan_type,
                  created_by: plan.created_by,
                  created_on: formatDate(plan.created_on),
                  cal: parseFloat(plan.total_cal) || 0,
                  adj_prot: parseFloat(plan.total_protein) || 0,
                  carb: parseFloat(plan.total_carb) || 0,
                  fat: parseFloat(plan.total_fat) || 0,
                  gl: parseFloat(plan.total_gl) || 0,
                  status: status,
                };
              },
            );

            setMealPlans(mappedPlans);
            console.log(
              "✅ Meal plans set successfully:",
              mappedPlans.length,
              "plans",
            );
          } else {
            const errorMsg =
              apiResponse.message || "Failed to fetch meal plans";
            console.error("❌ API returned error:", errorMsg);
            setApiError(errorMsg);
          }
        } catch (err) {
          const errorMsg =
            err instanceof Error ? err.message : "Failed to load meal plans";
          console.error("❌ Error fetching meal plans:", err);
          setApiError(errorMsg);
        } finally {
          if (!silent) {
            setLoading(false);
          }
          console.log("🏁 Meal plans fetch completed");
        }
      },
      [userId],
    );

    useEffect(() => {
      const handleMealPlanUpdate = () => {
        console.log("🔄 Received mealPlanUpdated event - refreshing silently");
        fetchMealPlans(true);
      };

      window.addEventListener("mealPlanUpdated", handleMealPlanUpdate);
      return () => {
        window.removeEventListener("mealPlanUpdated", handleMealPlanUpdate);
      };
    }, [fetchMealPlans]);

    // Expose refresh method via ref
    useImperativeHandle(ref, () => ({
      refresh: fetchMealPlans,
    }));

    useEffect(() => {
      fetchMealPlans(false);
    }, [fetchMealPlans]);

    // Generate next plan name
    useEffect(() => {
      if (mealPlans.length === 0) {
        setNextPlanName("TRV/M01");
        return;
      }

      const planNumbers = mealPlans
        .map((plan) => {
          const match = plan.plan_name.match(/TRV\/M0*(\d+)/);
          return match ? parseInt(match[1], 10) : 0;
        })
        .filter((num) => num > 0);

      const maxNumber = planNumbers.length > 0 ? Math.max(...planNumbers) : 0;
      const nextNum = maxNumber + 1;
      setNextPlanName(`TRV/M${String(nextNum).padStart(2, "0")}`);
    }, [mealPlans]);

    const getStatusColor = (status: string) => {
      switch (status) {
        case "Active":
          return "bg-[#EBF7F4] text-[#0DB5B5]";
        case "Approval Pending":
          return "bg-[#FFF3DE] text-[#FF9500]";
        case "Draft":
          return "bg-[#E9E9E9] text-[#202020]";
        case "In-Active":
          return "bg-[#FFDADA] text-[#FF3B30]";
        default:
          return "bg-gray-100 text-gray-600";
      }
    };

    // ── Toggle a single meal-type filter ────────────────────────────────────────
    const toggleMealTypeFilter = (type: string) => {
      setSelectedMealTypeFilters((prev) =>
        prev.includes(type) ? prev.filter((t) => t !== type) : [...prev, type],
      );
    };
    // ────────────────────────────────────────────────────────────────────────────

    // ── Filtering logic ─────────────────────────────────────────────────────────
    const filteredPlans = mealPlans.filter((plan) => {
      const matchesStatusFilter =
        activeFilter === "all" ||
        (activeFilter === "inactive" && plan.status === "In-Active") ||
        plan.status.toLowerCase().replace(" ", "").replace("-", "") ===
          activeFilter;

      const matchesMealTypeFilter =
        selectedMealTypeFilters.length === 0 ||
        selectedMealTypeFilters.includes(plan.meal_type);

      const matchesSearch = plan.plan_name
        .toLowerCase()
        .includes(searchQuery.toLowerCase());

      return matchesStatusFilter && matchesMealTypeFilter && matchesSearch;
    });
    // ────────────────────────────────────────────────────────────────────────────

    const handleCreateMealPlan = (data: any) => {
      try {
        console.log("New meal plan data:", {
          plan_name: nextPlanName,
          ...data,
        });

        const currentUserName = user
          ? `${user.nutrist_fname || ""} ${user.nutrist_lname || ""}`.trim()
          : "Unknown";

        const newMealPlan: MealPlan = {
          id:
            mealPlans.length > 0
              ? Math.max(...mealPlans.map((p) => p.id)) + 1
              : 1,
          plan_name: nextPlanName,
          plan_code: "",
          meal_type: data.planType,
          created_by: currentUserName,
          created_on: new Date()
            .toLocaleDateString("en-GB", {
              day: "2-digit",
              month: "short",
              year: "numeric",
            })
            .replace(/ /g, " "),
          cal: parseFloat(data.totalCalories.max) || 0,
          adj_prot: parseFloat(data.totalAdjProtein.max) || 0,
          carb: parseFloat(data.totalNetCarbs.max) || 0,
          fat: parseFloat(data.totalFats.max) || 0,
          gl: parseFloat(data.totalGL.max) || 0,
          status: "Draft",
        };

        setMealPlans((prevPlans) => [newMealPlan, ...prevPlans]);

        success(
          "Meal plan created successfully",
          `${nextPlanName} has been created and saved as draft.`,
          4000,
        );
      } catch (err) {
        console.error("Error creating meal plan:", err);
        error(
          "Failed to create meal plan",
          "An error occurred while creating the meal plan. Please try again.",
          4000,
        );
      }
    };

    const handleRowClick = (plan: MealPlan) => {
      navigate(`/client/${userId}/plans/meal/${plan.id}`, {
        state: { mealPlan: plan, isDeactivated },
      });
    };

    const handleMenuClick = (event: React.MouseEvent, planId: number) => {
      event.stopPropagation();

      if (openDropdownId === planId) {
        setOpenDropdownId(null);
        return;
      }

      const button = event.currentTarget as HTMLElement;
      const rect = button.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      const openUpward = spaceBelow < DROPDOWN_HEIGHT + 8;

      setDropdownPosition(
        openUpward
          ? { bottom: window.innerHeight - rect.top + 5, left: rect.left - 120 }
          : { top: rect.bottom + 5, left: rect.left - 120 },
      );

      setOpenDropdownId(planId);
    };

    const handleEdit = (event: React.MouseEvent, plan: MealPlan) => {
      event.stopPropagation();
      setOpenDropdownId(null);
      navigate(`/client/${userId}/plans/meal/${plan.id}`, {
        state: { mealPlan: plan, isDeactivated },
      });
    };

    const handleDelete = (event: React.MouseEvent, plan: MealPlan) => {
      event.stopPropagation();
      setOpenDropdownId(null);
      setPlanToDelete(plan);
      setIsDeletePopupOpen(true);
    };

    const handleConfirmDelete = async () => {
      if (!planToDelete) return;

      try {
        setIsDeleting(true);
        console.log("🗑️ Deleting meal plan:", planToDelete.plan_name);

        const apiResponse = await deleteMealPlan({
          user_id: userId,
          meal_plan_id: planToDelete.id,
        });

        if (apiResponse.success === "1") {
          setMealPlans((prevPlans) =>
            prevPlans.filter((p) => p.id !== planToDelete.id),
          );
          success(
            "Meal plan deleted",
            `${planToDelete.plan_code || planToDelete.plan_name} has been deleted successfully.`,
            3000,
          );
          setIsDeletePopupOpen(false);
          setPlanToDelete(null);
        } else {
          throw new Error(apiResponse.message || "Failed to delete meal plan");
        }
      } catch (err) {
        const errorMsg =
          err instanceof Error ? err.message : "Failed to delete meal plan";
        console.error("❌ Error deleting meal plan:", err);
        error("Delete failed", errorMsg, 4000);
      } finally {
        setIsDeleting(false);
      }
    };

    const handleCancelDelete = () => {
      setIsDeletePopupOpen(false);
      setPlanToDelete(null);
    };

    const handleDuplicate = (event: React.MouseEvent, plan: MealPlan) => {
      event.stopPropagation();
      setOpenDropdownId(null);
      setPlanToDuplicate(plan);
      setIsDuplicatePopupOpen(true);
    };

    const handleConfirmDuplicate = async () => {
      if (!planToDuplicate) return;

      try {
        setIsDuplicating(true);
        console.log(
          "📋 Fetching meal plan details for duplication:",
          planToDuplicate.plan_name,
        );

        const apiResponse = await getMealPlanDetail({
          user_id: userId.toString(),
          meal_plan_id: planToDuplicate.id.toString(),
        });
        console.log("✅ Meal plan details received:", apiResponse);

        if (apiResponse.success === "1" && apiResponse.nutrient_max_values) {
          const nutrientData = apiResponse.nutrient_max_values;

          setDuplicatePlanData({
            planType: apiResponse.meal_plan_type || "",
            totalCalories: {
              min: nutrientData.total_calories_min || "",
              max: nutrientData.total_calories_max || "",
            },
            totalAdjProtein: {
              min: nutrientData.total_protein_min || "",
              max: nutrientData.total_protein_max || "",
            },
            totalNetCarbs: {
              min: nutrientData.total_carbs_min || "",
              max: nutrientData.total_carbs_max || "",
            },
            totalFats: {
              min: nutrientData.total_fats_min || "",
              max: nutrientData.total_fats_max || "",
            },
            totalGL: {
              min: nutrientData.total_GL_min || "",
              max: nutrientData.total_GL_max || "",
            },
            cpfRatio: {
              min: { c: "0", p: "0", f: "0" },
              max: { c: "0", p: "0", f: "0" },
            },
            instructions: apiResponse.instructions || "",
            sourceMealPlanId: planToDuplicate.id.toString(),
          });

          setIsDuplicatePopupOpen(false);
          setIsCreatePopupOpen(true);
        } else {
          throw new Error(
            apiResponse.message || "Failed to fetch meal plan details",
          );
        }
      } catch (err) {
        const errorMsg =
          err instanceof Error
            ? err.message
            : "Failed to fetch meal plan details";
        console.error("❌ Error fetching meal plan details:", err);
        error("Duplication failed", errorMsg, 4000);
      } finally {
        setIsDuplicating(false);
        setPlanToDuplicate(null);
      }
    };

    const handleCancelDuplicate = () => {
      setIsDuplicatePopupOpen(false);
      setPlanToDuplicate(null);
    };

    const handleSuggestAsTemplate = (event: React.MouseEvent, plan: MealPlan) => {
      event.stopPropagation();
      setOpenDropdownId(null);
      setPlanToSuggest(plan);
      setSuggestTemplateName("");
      setSuggestionNote("");
      setIsSuggestTemplateOpen(true);
    };

    const handleConfirmSuggestTemplate = async () => {
      if (!planToSuggest || !user) return;

      try {
        setIsSuggesting(true);

        const payload: Record<string, unknown> = {
          plan_id: planToSuggest.id,
          nutritionist_id: user.nutrist_id,
        };
        if (suggestTemplateName.trim())
          payload.suggested_template_name = suggestTemplateName.trim();
        if (suggestionNote.trim())
          payload.suggestion_note = suggestionNote.trim();

        const apiResponse = await suggestPlanGlobalTemplate(payload);

        if (apiResponse.success) {
          success(
            "Template suggested",
            `${planToSuggest.plan_code || planToSuggest.plan_name} has been suggested as a global template.`,
            4000,
          );
          setIsSuggestTemplateOpen(false);
          setPlanToSuggest(null);
        } else {
          throw new Error(apiResponse.message || "Failed to suggest template");
        }
      } catch (err) {
        const errorMsg =
          err instanceof Error ? err.message : "Failed to suggest template";
        console.error("❌ Error suggesting template:", err);
        error("Suggestion failed", errorMsg, 4000);
      } finally {
        setIsSuggesting(false);
      }
    };

    const handleCancelSuggestTemplate = () => {
      setIsSuggestTemplateOpen(false);
      setPlanToSuggest(null);
      setSuggestTemplateName("");
      setSuggestionNote("");
    };

    const handleCreatePopupClose = () => {
      setIsCreatePopupOpen(false);
      setDuplicatePlanData(null);
    };

    return (
      <>
        <style>{shimmerStyles}</style>

        <ToastContainer toasts={toasts} onClose={removeToast} />

        <div className="h-full flex flex-col bg-white rounded-md border border-[#F3F4F6] overflow-hidden">
          {/* Fixed Header */}
          <div className="flex-shrink-0 px-6 py-4">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold font-inter text-[#1F1F1F]">
                Meal plan
              </h2>
              <div className="flex items-center gap-3">
                {/* Search */}
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#8E8E93]" />
                  <input
                    type="text"
                    placeholder="Search.."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10 pr-4 py-2 border border-[#E0E0E0] rounded-md text-sm font-inter focus:outline-none focus:border-[#0DB5B5] w-64"
                  />
                </div>

                {/* ── Filter button — always visible ─────────────────────── */}
                <button
                  ref={filterButtonRef}
                  onClick={() => {
                    if (!isFilterOpen && filterButtonRef.current) {
                      const rect =
                        filterButtonRef.current.getBoundingClientRect();
                      setFilterDropdownPosition({
                        top: rect.bottom + 6,
                        left: rect.right - 220,
                      });
                    }
                    setIsFilterOpen((prev) => !prev);
                  }}
                  className={`flex items-center gap-2 px-4 py-2 border rounded-md text-sm font-inter transition-colors ${
                    selectedMealTypeFilters.length > 0
                      ? "border-[#0DB5B5] text-[#0DB5B5] bg-[#F0FAFA]"
                      : "border-[#E0E0E0] text-[#1F1F1F] hover:border-[#0DB5B5]"
                  }`}
                >
                  <Filter className="w-4 h-4" />
                  Filter
                  {selectedMealTypeFilters.length > 0 && (
                    <span className="ml-1 bg-[#0DB5B5] text-white text-xs rounded-full w-4 h-4 flex items-center justify-center leading-none">
                      {selectedMealTypeFilters.length}
                    </span>
                  )}
                </button>

                {/* ── Create button — disabled when deactivated ──────────── */}
                <PermissionGate resource="meal_plan" action="create">
                  <Button
                    variant="primary"
                    onClick={() => !isDeactivated && navigate(`/client/${userId}/plans/meal/create`, { state: { planName: nextPlanName } })}
                    disabled={isDeactivated}
                    startIcon={<Plus className="w-4 h-4" />}
                  >
                    Create
                  </Button>
                </PermissionGate>
              </div>
            </div>
          </div>

          {/* Fixed Tab Navigation */}
          <div className="flex-shrink-0 px-6 pt-2 border-b border-[#F3F4F6]">
            <div className="flex gap-6">
              {[
                { id: "all", label: "All" },
                { id: "active", label: "Active" },
                { id: "draft", label: "Draft" },
                { id: "inactive", label: "Inactive" },
              ].map((filter) => (
                <button
                  key={filter.id}
                  onClick={() => setActiveFilter(filter.id)}
                  className={`pb-2 font-inter text-sm font-medium transition-colors relative ${
                    activeFilter === filter.id
                      ? "text-[#0DB5B5]"
                      : "text-[#8E8E93] hover:text-[#1F1F1F]"
                  }`}
                >
                  {filter.label}
                  {activeFilter === filter.id && (
                    <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#0DB5B5]" />
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* Scrollable Table Container */}
          <div className="flex-1 overflow-hidden flex flex-col">
            {/* Fixed Table Header */}
            <div className="flex-shrink-0 overflow-x-auto bg-white border-b border-[#111C2D33] custom-scrollbar-overlay">
              <table className="w-full min-w-[900px] table-fixed">
                <thead>
                  <tr className="bg-white">
                    <th className="text-left pt-6 pb-3 px-4 text-[#646464] text-sm 3xl:text-base font-medium font-inter whitespace-nowrap w-[3%]">
                      Sl
                    </th>
                    <th className="text-left pt-6 pb-3 px-4 text-[#646464] text-sm 3xl:text-base font-medium font-inter whitespace-nowrap w-[18%]">
                      Plan name
                    </th>
                    <th className="text-left pt-6 pb-3 px-4 text-[#646464] text-sm 3xl:text-base font-medium font-inter whitespace-nowrap w-[12%]">
                      Meal Type
                    </th>
                    <th className="text-left pt-6 pb-3 px-4 text-[#646464] text-sm 3xl:text-base font-medium font-inter whitespace-nowrap w-[12%]">
                      Created By
                    </th>
                    <th className="text-left pt-6 pb-3 px-4 text-[#646464] text-sm 3xl:text-base font-medium font-inter whitespace-nowrap w-[10%]">
                      Created On
                    </th>
                    <th className="text-left pt-6 pb-3 px-4 text-[#646464] text-sm 3xl:text-base font-medium font-inter whitespace-nowrap w-[8%]">
                      Cal
                    </th>
                    <th className="text-left pt-6 pb-3 px-4 text-[#646464] text-sm 3xl:text-base font-medium font-inter whitespace-nowrap w-[8%]">
                      Adj. Prot
                    </th>
                    <th className="text-left pt-6 pb-3 px-4 text-[#646464] text-sm 3xl:text-base font-medium font-inter whitespace-nowrap w-[8%]">
                      Carb
                    </th>
                    <th className="text-left pt-6 pb-3 px-4 text-[#646464] text-sm 3xl:text-base font-medium font-inter whitespace-nowrap w-[8%]">
                      Fat
                    </th>
                    <th className="text-left pt-6 pb-3 px-4 text-[#646464] text-sm 3xl:text-base font-medium font-inter whitespace-nowrap w-[8%]">
                      GL
                    </th>
                    {/* Only show action column header when not deactivated */}
                    <th className="text-right pt-6 pb-3 px-4 text-[#646464] text-sm 3xl:text-base font-medium font-inter whitespace-nowrap w-[5%]" />
                  </tr>
                </thead>
              </table>
            </div>

            {/* Scrollable Table Body */}
            <div className="flex-1 overflow-x-auto overflow-y-auto custom-scrollbar-overlay">
              {loading ? (
                <div className="h-full shimmer" />
            
              ) : (
                <>
                  <table className="w-full min-w-[900px] table-fixed">
                    <tbody className="bg-white divide-y divide-[#F3F4F6]">
                      {filteredPlans.map((plan, index) => (
                        <tr
                          key={plan.id}
                          onClick={() => handleRowClick(plan)}
                          className="hover:bg-[#F9FEFE] transition-colors cursor-pointer"
                        >
                          <td className="px-4 py-4 whitespace-nowrap text-sm 3xl:text-base font-medium font-inter text-[#1F1F1F] w-[3%]">
                            {String(index + 1).padStart(2, "0")}
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap w-[18%]">
                            <div className="flex items-center gap-2">
                              <span className="text-sm 3xl:text-base font-medium font-inter text-[#1F1F1F]">
                                {plan.plan_code || plan.plan_name}
                              </span>
                              <span
                                className={`px-1.5 py-0.5 rounded-[24px] text-[8px] 3xl:text-[10px] font-semibold font-inter flex-shrink-0 ${getStatusColor(plan.status)}`}
                              >
                                {plan.status}
                              </span>
                            </div>
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap text-sm 3xl:text-base font-medium font-inter text-[#1F1F1F] w-[12%]">
                            <SmartTooltipCell value={plan.meal_type}>
                              {plan.meal_type}
                            </SmartTooltipCell>
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap text-sm 3xl:text-base font-medium font-inter text-[#1F1F1F] w-[12%]">
                            <SmartTooltipCell
                              value={
                                isCreatedByCurrentUser(plan.created_by)
                                  ? "You"
                                  : plan.created_by
                              }
                            >
                              {isCreatedByCurrentUser(plan.created_by)
                                ? "You"
                                : plan.created_by}
                            </SmartTooltipCell>
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap text-sm 3xl:text-base font-medium font-inter text-[#1F1F1F] w-[10%]">
                            {plan.created_on}
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap text-sm 3xl:text-base font-medium font-inter text-[#1F1F1F] w-[8%]">
                            {plan.cal}
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap text-sm 3xl:text-base font-medium font-inter text-[#1F1F1F] w-[8%]">
                            {plan.adj_prot}
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap text-sm 3xl:text-base font-medium font-inter text-[#1F1F1F] w-[8%]">
                            {plan.carb}
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap text-sm 3xl:text-base font-medium font-inter text-[#1F1F1F] w-[8%]">
                            {plan.fat}
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap text-sm 3xl:text-base font-medium font-inter text-[#1F1F1F] w-[8%]">
                            {plan.gl}
                          </td>

                          {/* ── 3-dot menu cell — hidden for deactivated clients ── */}
                          <td className="px-4 py-4 whitespace-nowrap text-right w-[5%] relative">
                            {!isDeactivated &&
                              // Draft rows only need the menu when the user can edit or delete.
                              // Active / In-Active rows always have Duplicate + Suggest options.
                              (plan.status === "Draft"
                                ? can("meal_plan", "edit") || can("meal_plan", "delete")
                                : true) && (
                              <button
                                onClick={(e) => handleMenuClick(e, plan.id)}
                                className="p-1 hover:bg-gray-100 rounded transition-colors inline-flex"
                              >
                                <MoreVertical className="w-5 h-5 text-[#646464]" />
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>

                  {filteredPlans.length === 0 && (
                    <div className="px-6 py-12 text-center">
                      <p className="text-sm text-gray-500">
                        No meal plans found
                      </p>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>

        {/* Row-action Dropdown Menu — never shown for deactivated clients */}
        {openDropdownId !== null && !isDeactivated && (
          <div
            ref={dropdownRef}
            className="dropdown-menu"
            style={{
              ...(dropdownPosition.top !== undefined
                ? { top: `${dropdownPosition.top}px` }
                : { bottom: `${dropdownPosition.bottom}px` }),
              left: `${dropdownPosition.left}px`,
            }}
          >
            {(() => {
              const plan = mealPlans.find((p) => p.id === openDropdownId);
              if (!plan) return null;

              if (plan.status === "Draft") {
                const canEdit = can("meal_plan", "edit");
                const canDelete = can("meal_plan", "delete");
                return (
                  <>
                    {canEdit && (
                      <button
                        className="dropdown-item"
                        onClick={(e) => handleEdit(e, plan)}
                      >
                        Edit
                      </button>
                    )}
                    {canEdit && canDelete && <div className="dropdown-divider" />}
                    {canDelete && (
                      <button
                        className="dropdown-item delete"
                        onClick={(e) => handleDelete(e, plan)}
                      >
                        Delete
                      </button>
                    )}
                  </>
                );
              } else if (
                plan.status === "Active" ||
                plan.status === "In-Active"
              ) {
                return (
                  <>
                    <button
                      className="dropdown-item"
                      onClick={(e) => handleDuplicate(e, plan)}
                    >
                      Duplicate
                    </button>
                    <div className="dropdown-divider" />
                    <button
                      className="dropdown-item"
                      onClick={(e) => handleSuggestAsTemplate(e, plan)}
                    >
                      Suggest as template
                    </button>
                  </>
                );
              }
              return null;
            })()}
          </div>
        )}

        {/* ── Filter Dropdown Portal ─────────────────────────────────────────────── */}
        {isFilterOpen &&
          ReactDOM.createPortal(
            <div
              ref={filterDropdownRef}
              style={{
                position: "fixed",
                top: `${filterDropdownPosition.top}px`,
                left: `${filterDropdownPosition.left}px`,
                zIndex: 1000,
                width: "220px",
              }}
              className="bg-white border border-[#E0E0E0] rounded-lg shadow-lg overflow-hidden"
            >
              {/* Dropdown Header */}
              <div className="flex items-center justify-between px-4 py-3 border-b border-[#F3F4F6]">
                <span className="text-sm font-semibold font-inter text-[#1F1F1F]">
                  Meal type
                </span>
                <div className="flex items-center gap-3">
                  {selectedMealTypeFilters.length > 0 && (
                    <button
                      onClick={() => setSelectedMealTypeFilters([])}
                      className="text-xs text-[#0DB5B5] hover:text-[#0CA0A0] font-inter font-medium transition-colors"
                    >
                      Clear all
                    </button>
                  )}
                  <button
                    onClick={() => setIsFilterOpen(false)}
                    className="text-[#8E8E93] hover:text-[#1F1F1F] transition-colors"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              </div>

              {/* Scrollable options list */}
              <div
                className="overflow-y-auto custom-scrollbar-overlay"
                style={{ maxHeight: "280px" }}
              >
                {isFilterTypesLoading ? (
                  <div className="flex items-center justify-center py-6">
                    <div className="w-5 h-5 border-2 border-[#0DB5B5] border-t-transparent rounded-full animate-spin" />
                  </div>
                ) : filterPlanTypes.length === 0 ? (
                  <p className="px-4 py-4 text-sm text-[#8E8E93] font-inter">
                    No plan types available
                  </p>
                ) : (
                  filterPlanTypes.map((type) => {
                    const isChecked = selectedMealTypeFilters.includes(type);
                    return (
                      <div
                        key={type}
                        onClick={() => toggleMealTypeFilter(type)}
                        className="flex items-center gap-3 px-4 py-2.5 cursor-pointer hover:bg-[#F9FEFE] transition-colors select-none"
                      >
                        {/* Custom checkbox */}
                        <div
                          className={`w-4 h-4 rounded flex-shrink-0 border flex items-center justify-center transition-colors ${
                            isChecked
                              ? "bg-[#0DB5B5] border-[#0DB5B5]"
                              : "bg-white border-[#C7C7CC]"
                          }`}
                        >
                          {isChecked && (
                            <svg
                              className="w-2.5 h-2.5 text-white"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth={3}
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                d="M5 13l4 4L19 7"
                              />
                            </svg>
                          )}
                        </div>
                        <span
                          className={`text-sm font-inter ${
                            isChecked
                              ? "text-[#1F1F1F] font-medium"
                              : "text-[#3C3C43]"
                          }`}
                        >
                          {type}
                        </span>
                      </div>
                    );
                  })
                )}
              </div>
            </div>,
            document.body,
          )}
        {/* ──────────────────────────────────────────────────────────────────────── */}

        {/* Meal Plan Create Popup — never opened when deactivated */}
        {!isDeactivated && (
          <MealPlanDuplicateAndTimingsPopup
            isOpen={isCreatePopupOpen}
            onClose={handleCreatePopupClose}
            onSubmit={handleCreateMealPlan}
            userId={userId}
            duplicateData={duplicatePlanData}
          />
        )}

        {/* Delete Confirmation Popup */}
        {isDeletePopupOpen && planToDelete && (
          <div className="fixed inset-0 z-50 flex items-center justify-center">
            <div
              className="absolute inset-0 bg-black bg-opacity-50 transition-opacity"
              onClick={handleCancelDelete}
            />
            <div className="relative bg-white rounded-lg shadow-xl w-full max-w-md mx-4 overflow-hidden">
              <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-semibold font-inter text-[#1F1F1F]">
                  Delete Meal Plan
                </h3>
                <button
                  onClick={handleCancelDelete}
                  disabled={isDeleting}
                  className="p-1 hover:bg-gray-100 rounded-full transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <X className="w-5 h-5 text-gray-500" />
                </button>
              </div>
              <div className="px-6 py-6">
                <div className="flex items-start gap-4">
                  <div className="flex-1">
                    <p className="text-sm font-inter text-gray-700 mb-2">
                      Are you sure you want to delete{" "}
                      <span className="font-semibold text-[#1F1F1F]">
                        {planToDelete.plan_code || planToDelete.plan_name}
                      </span>
                      ?
                    </p>
                    <p className="text-sm font-inter text-gray-500">
                      This action cannot be undone. All meal plan data and
                      related information will be permanently removed.
                    </p>
                  </div>
                </div>
              </div>
              <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200">
                <button
                  onClick={handleCancelDelete}
                  disabled={isDeleting}
                  className="px-4 py-2 border border-gray-300 rounded-md text-sm font-inter font-medium text-gray-700 hover:bg-gray-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Cancel
                </button>
                <button
                  onClick={handleConfirmDelete}
                  disabled={isDeleting}
                  className="px-4 py-2 bg-red-600 text-white rounded-md text-sm font-inter font-medium hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {isDeleting ? (
                    <>
                      <svg
                        className="animate-spin h-4 w-4 text-white"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        />
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        />
                      </svg>
                      Deleting...
                    </>
                  ) : (
                    "Delete"
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Suggest as Template Popup */}
        {isSuggestTemplateOpen && planToSuggest && (
          <div className="fixed inset-0 z-50 flex items-center justify-center">
            <div
              className="absolute inset-0 bg-black bg-opacity-50 transition-opacity"
              onClick={handleCancelSuggestTemplate}
            />
            <div className="relative bg-white rounded-lg shadow-xl w-full max-w-md mx-4 overflow-hidden">
              <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-semibold font-inter text-[#1F1F1F]">
                  Suggest as template
                </h3>
                <button
                  onClick={handleCancelSuggestTemplate}
                  disabled={isSuggesting}
                  className="p-1 hover:bg-gray-100 rounded-full transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <X className="w-5 h-5 text-gray-500" />
                </button>
              </div>
              <div className="px-6 py-6 flex flex-col gap-4">
                <p className="text-sm font-inter text-gray-600">
                  Suggest{" "}
                  <span className="font-semibold text-[#1F1F1F]">
                    {planToSuggest.plan_code || planToSuggest.plan_name}
                  </span>{" "}
                  as a global template. The Following filed are optional.
                </p>
                <div className="flex flex-col gap-1.5">
                  <label className="text-xs font-medium font-inter text-[#646464]">
                    Template name
                  </label>
                  <input
                    type="text"
                    value={suggestTemplateName}
                    onChange={(e) => setSuggestTemplateName(e.target.value)}
                    placeholder="e.g. Gut Health Regular"
                    disabled={isSuggesting}
                    className="w-full px-3 py-2 border border-[#E0E0E0] rounded-md text-sm font-inter focus:outline-none focus:border-[#0DB5B5] disabled:bg-gray-50 disabled:cursor-not-allowed"
                  />
                </div>
                {/* <div className="flex flex-col gap-1.5">
                  <label className="text-xs font-medium font-inter text-[#646464]">
                    Suggestion note
                  </label>
                  <textarea
                    value={suggestionNote}
                    onChange={(e) => setSuggestionNote(e.target.value)}
                    placeholder="e.g. Works great for gut health clients..."
                    rows={3}
                    disabled={isSuggesting}
                    className="w-full px-3 py-2 border border-[#E0E0E0] rounded-md text-sm font-inter focus:outline-none focus:border-[#0DB5B5] resize-none disabled:bg-gray-50 disabled:cursor-not-allowed"
                  />
                </div> */}
              </div>
              <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200">
                <Button
                  variant="secondary"
                  size="md"
                  onClick={handleCancelSuggestTemplate}
                  disabled={isSuggesting}
                >
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  size="md"
                  onClick={handleConfirmSuggestTemplate}
                  disabled={isSuggesting}
                >
                  {isSuggesting ? (
                    <>
                      <svg
                        className="animate-spin h-4 w-4 text-white"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        />
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        />
                      </svg>
                      Suggesting...
                    </>
                  ) : (
                    "Suggest"
                  )}
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Duplicate Confirmation Popup */}
        {isDuplicatePopupOpen && planToDuplicate && (
          <div className="fixed inset-0 z-50 flex items-center justify-center">
            <div
              className="absolute inset-0 bg-black bg-opacity-50 transition-opacity"
              onClick={handleCancelDuplicate}
            />
            <div className="relative bg-white rounded-lg shadow-xl w-full max-w-md mx-4 overflow-hidden">
              <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-semibold font-inter text-[#1F1F1F]">
                  Duplicate meal plan confirmation?
                </h3>
                <button
                  onClick={handleCancelDuplicate}
                  disabled={isDuplicating}
                  className="p-1 hover:bg-gray-100 rounded-full transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <X className="w-5 h-5 text-gray-500" />
                </button>
              </div>
              <div className="px-6 py-6">
                <p className="text-sm font-inter text-gray-700">
                  Are you sure you want to duplicate this meal plan? All
                  existing meals and details will be copied to a new plan.
                </p>
              </div>
              <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200">
                <Button
                  variant="secondary"
                  size="md"
                  onClick={handleCancelDuplicate}
                  disabled={isDuplicating}
                >
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  size="md"
                  onClick={handleConfirmDuplicate}
                  disabled={isDuplicating}
                >
                  {isDuplicating ? (
                    <>
                      <svg
                        className="animate-spin h-4 w-4 text-white"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        />
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        />
                      </svg>
                      Loading...
                    </>
                  ) : (
                    "Confirm"
                  )}
                </Button>
              </div>
            </div>
          </div>
        )}
      </>
    );
  },
);

MealPlanTab.displayName = "MealPlanTab";

export default MealPlanTab;